import { conversions, triggers } from '@bb/contracts';
import { conditionIssue, MAX_PROPERTY_FILTERS, type ActionBasedIssue } from '../trigger/actionBased.ts';

export type Conversions = conversions.Conversions;
export type ConversionEvent = conversions.ConversionEvent;
export type ConversionKind = ConversionEvent['kind'];

export const CONVERSIONS_MAX = conversions.CONVERSIONS_MAX;
export const DEFAULT_CONVERSIONS = conversions.DEFAULT_CONVERSIONS;

/** The window is entered in hours or days and stored in seconds. */
export type WindowUnit = 'hours' | 'days';
export const WINDOW_UNIT_SECONDS: Record<WindowUnit, number> = { hours: 3_600, days: 86_400 };
export const WINDOW_MAX_DAYS = conversions.CONVERSION_WINDOW_MAX_SECONDS / WINDOW_UNIT_SECONDS.days;
export const WINDOW_DEFAULT_DAYS = conversions.CONVERSION_WINDOW_DEFAULT_SECONDS / WINDOW_UNIT_SECONDS.days;

/** Days when the window is whole days (`259200` → 3 days), hours otherwise. */
export const windowUnitOf = (seconds: number): WindowUnit => (seconds > 0 && seconds % WINDOW_UNIT_SECONDS.days === 0 ? 'days' : 'hours');

export const windowSeconds = (amount: number, unit: WindowUnit): number => Math.round(amount * WINDOW_UNIT_SECONDS[unit]);

/** A new conversion event with its own id and the default window of three days; a custom event still has to be chosen. */
export function newConversion(kind: ConversionKind = 'custom_event', id: string = crypto.randomUUID()): ConversionEvent {
  const windowSeconds = conversions.CONVERSION_WINDOW_DEFAULT_SECONDS;
  return kind === 'custom_event' ? { id, kind, event: '', propertyFilters: [], windowSeconds } : { id, kind, windowSeconds };
}

/** The conversion as another kind: its id and window stay, the event and its filters do not carry over. */
export function withKind(conversion: ConversionEvent, kind: ConversionKind): ConversionEvent {
  if (conversion.kind === kind) return conversion;
  return { ...newConversion(kind, conversion.id), windowSeconds: conversion.windowSeconds };
}

/** The conversion at `index` moved to the front, where it is primary; the others keep their order. */
export function makePrimary(list: Conversions, index: number): Conversions {
  const chosen = list[index];
  if (!chosen || index === 0) return list;
  return [chosen, ...list.filter((_, i) => i !== index)];
}

/** A stored draft may hold the contract's input shape (no property filters on a custom event): the default is filled in. */
export function withConversionDefaults(value: Conversions | undefined): Conversions {
  return (value ?? []).map((c) => (c.kind === 'custom_event' ? { ...c, propertyFilters: c.propertyFilters ?? [] } : c));
}

// ---------------------------------------------------------------------------------------------------------------
// Validation

const FIELD_MESSAGES: Record<string, string> = {
  id: 'This conversion event has no valid id: remove it and add it again.',
  kind: 'Choose what the user does.',
  event: 'Choose an event.',
  windowSeconds: `Enter a window from 1 hour to ${WINDOW_MAX_DAYS} days.`,
  path: 'Choose a property.',
  dataType: 'Choose how to compare.',
  operator: 'Choose an operator.',
};

type ZodIssue = { path: PropertyKey[]; code: string; message: string };

function messageOf(issue: ZodIssue, path: Array<string | number>): string {
  if (path.length === 0) return issue.code === 'too_big' ? `Use at most ${CONVERSIONS_MAX} conversion events.` : 'Each conversion event needs its own id: remove the copy and add it again.';
  const last = path[path.length - 1];
  if (last === 'propertyFilters' && issue.code === 'too_big') return `Use at most ${MAX_PROPERTY_FILTERS} property filters.`;
  const key = [...path].reverse().find((p): p is string => typeof p === 'string');
  return (key && FIELD_MESSAGES[key]) ?? issue.message.charAt(0).toUpperCase() + issue.message.slice(1);
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const covers = (reported: readonly ActionBasedIssue[], at: Array<string | number>) => reported.some((r) => at.every((p, k) => r.path[k] === p));

/**
 * Every issue of the conversions with the path of its field (`[1, 'windowSeconds']`; `[]` for the list): the server's
 * schema (`conversions.Conversions`) and the operand rules of property filters it leaves open, so `amount >= (nothing)`
 * is never saved.
 */
export function conversionIssueList(value: unknown): ActionBasedIssue[] {
  const parsed = conversions.Conversions.safeParse(value);
  const issues: ActionBasedIssue[] = parsed.success
    ? []
    : parsed.error.issues.map((issue) => {
        const path = issue.path.filter((p): p is string | number => typeof p !== 'symbol');
        return { path, message: messageOf(issue, path) };
      });
  asArray(value).forEach((raw, i) => {
    const conversion = raw as { kind?: unknown; propertyFilters?: unknown } | null;
    if (conversion?.kind !== 'custom_event') return;
    asArray(conversion.propertyFilters).forEach((filter, j) => {
      const at = [i, 'propertyFilters', j];
      const condition = triggers.PropertyCondition.safeParse(filter);
      if (!condition.success || covers(issues, at)) return;
      const issue = conditionIssue(condition.data);
      if (issue) issues.push({ path: [...at, issue.field], message: issue.message });
    });
  });
  return issues;
}

/**
 * Client-side check of the contract, one message per field path: `conversions` for the list, `0.event`,
 * `1.windowSeconds`, `2.propertyFilters.0.value`. Empty when the draft can be saved.
 */
export function conversionsIssues(value: unknown): Record<string, string> {
  const issues: Record<string, string> = {};
  for (const issue of conversionIssueList(value)) issues[issue.path.join('.') || 'conversions'] ??= issue.message;
  return issues;
}
